import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// Public-facing /insights/:slug — fetches the server-rendered HTML mirror
// from /api/insights/:slug, extracts the article body, and injects proper
// meta tags client-side for Googlebot's JS rendering pass. Server-side users
// who hit this route via a direct browser load see the SPA shell instantly;
// crawlers that fetch /api/insights/<slug> (canonical) get the SSR HTML.
export default function InsightsPage() {
  const { slug } = useParams();
  const [html, setHtml] = useState(null);
  const [meta, setMeta] = useState({ title: "", description: "" });
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const url = (process.env.REACT_APP_BACKEND_URL || "") + `/api/insights/${slug}`;
    fetch(url)
      .then((r) => r.ok ? r.text() : Promise.reject(r.status))
      .then((text) => {
        const titleMatch = text.match(/<title>([^<]+)<\/title>/);
        const descMatch = text.match(/<meta name="description" content="([^"]+)"/);
        const articleMatch = text.match(/<article>([\s\S]*?)<\/article>/);
        setMeta({
          title: titleMatch?.[1] || "Estima Insights",
          description: descMatch?.[1] || "",
        });
        setHtml(articleMatch?.[1] || "");
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  // Set document title + meta tags for SEO once we have the content.
  useEffect(() => {
    if (!meta.title) return;
    document.title = meta.title;
    let descTag = document.querySelector('meta[name="description"]');
    if (!descTag) {
      descTag = document.createElement("meta");
      descTag.setAttribute("name", "description");
      document.head.appendChild(descTag);
    }
    descTag.setAttribute("content", meta.description);
    // Canonical
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", `https://estima.co.in/insights/${slug}`);
  }, [meta, slug]);

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="insights-not-found">
        <h1 className="font-serif text-3xl mb-4">Insight not found</h1>
        <p className="text-muted-foreground">This article may have been moved or is still being prepared.</p>
        <a href="/" className="inline-block mt-6 text-[hsl(var(--secondary))] hover:underline">← estima.co.in</a>
      </div>
    );
  }

  if (!html) {
    return <div className="p-8 text-center text-muted-foreground" data-testid="insights-loading">Loading insight…</div>;
  }

  return (
    <div className="bg-[#FAFAF7] text-[#0A0908] min-h-screen" data-testid="insights-page">
      <article className="max-w-2xl mx-auto px-6 py-12 leading-relaxed">
        <nav className="mb-6">
          <a href="/" className="text-[#C85A32] text-xs hover:underline">← estima.co.in</a>
        </nav>
        <div className="text-[11px] uppercase tracking-widest text-[#C85A32] mb-2">Estima Insights</div>
        <h1 className="font-serif text-3xl sm:text-4xl mb-6 leading-tight">{meta.title}</h1>
        <div className="prose prose-sm prose-neutral max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
        <a
          href={`https://estima.co.in?utm_source=insights&utm_campaign=${slug}`}
          className="inline-block mt-8 bg-[#C85A32] text-white px-6 py-3 rounded font-semibold no-underline"
          data-testid="insights-cta"
        >
          Open Estima — your free property dashboard
        </a>
      </article>
    </div>
  );
}
